import { useEffect, useState } from 'react'
import { ClipboardList, Pencil, Plus } from 'lucide-react'
import {
  Bidi, Card, CardHeader, EmptyState, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  createChecklistTemplate, fetchChecklists, formatCount, may, translateError,
  type ChecklistTemplateBoard, type ChecklistTemplateRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { ChecklistEditor } from './ChecklistEditor'

/* ============================================================================
   W-125 · CHECKLIST TEMPLATES — what a clean consists of.

   **A LIVE TEMPLATE IS NEVER EDITED.** The rule is on the screen, in the person's own
   language, because it is the rule that explains everything else they are about to see:
   why "edit" produces a draft rather than an editable table, why publishing asks them to
   confirm a version number, and why the list has retired versions in it at all.

   The reason is not tidiness. Every completed clean records which version it followed, so
   a room cleaned in July can still be read against July's rules — and editing a live
   template in place would silently rewrite the history of every inspection that used it.
   An attendant would appear on record as having failed to do something that was not on
   their list. Those are exactly the disputes that reach a manager.

   THE EDITOR IS NOT A SEPARATE DESTINATION. W-126 is reached by pressing edit on a row and
   returns here when it is done — the two are one job, and a sidebar entry for "template
   editor" would be a link that is meaningless without a template already chosen.
   ========================================================================= */

export function Checklists({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [board, setBoard] = useState<ChecklistTemplateBoard | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)
  const [editing, setEditing] = useState<ChecklistTemplateRow | null>(null)
  const [creating, setCreating] = useState(false)

  if (!may('admin.checklists')) return <NoPermissionState />

  useEffect(() => {
    let live = true
    setError(null)
    fetchChecklists(propertyId)
      .then((next) => { if (live) setBoard(next) })
      .catch((caught: unknown) => { if (live) setError(caught) })
    return () => { live = false }
  }, [propertyId, reload])

  if (editing) {
    return (
      <ChecklistEditor
        propertyId={propertyId}
        template={editing}
        onDone={() => { setEditing(null); setReload((n) => n + 1) }}
      />
    )
  }

  if (error !== null && board === null) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => setReload((n) => n + 1)}
      />
    )
  }
  if (!board) return <LoadingState />

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.clHowVersionsWork} icon={<ClipboardList className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          {/* THE RULE, ON THE SCREEN. It is the one sentence that makes the rest of this
              screen make sense, and it belongs in front of the person rather than in a
              document they were sent in August. */}
          <p className="max-w-[70ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.clIntro}
          </p>

          <div className="flex flex-wrap items-center gap-2.5">
            <Figure label={t.clLive} value={formatCount(board.live, locale)} />
            <Figure label={t.clDrafts} value={formatCount(board.drafts, locale)}
              tone={board.drafts > 0 ? 'draft' : 'plain'} />
            <Figure label={t.clRetired} value={formatCount(board.retired, locale)} />

            <button
              type="button"
              onClick={() => setCreating(true)}
              className="ms-auto inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                         px-3 py-1.5 text-xs font-medium text-white cursor-pointer"
            >
              <Plus className="size-3.5" />
              {t.clNew}
            </button>
          </div>
        </div>
      </Card>

      {creating && (
        <NewTemplate
          propertyId={propertyId}
          locale={locale}
          t={t}
          onCancel={() => setCreating(false)}
          onCreated={(row) => { setCreating(false); setEditing(row) }}
        />
      )}

      {error !== null && (
        <ErrorState body={translateError(error, locale).message} onRetry={() => setError(null)} />
      )}

      <Card>
        <CardHeader
          title={t.clListTitle.replace('{count}', formatCount(board.templates.length, locale))}
          icon={<ClipboardList className="size-4" />}
        />
        {board.templates.length === 0
          ? <div className="p-3.5"><EmptyState title={t.clEmpty} body="" /></div>
          : (
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-[11px] text-muted-foreground">
                  <th className="p-2 text-start font-medium">{t.clColTemplate}</th>
                  <th className="p-2 text-start font-medium">{t.clColAppliesTo}</th>
                  <th className="p-2 text-start font-medium">{t.clColVersion}</th>
                  <th className="p-2 text-start font-medium">{t.clColItems}</th>
                  {/* A CREDITS COLUMN STOOD HERE AND IT WAS THE SAME NUMBER AS THE ONE
                      BESIDE IT. It came from `TaskType.Credits`, which was the standard
                      time over a divisor nobody had written down — defect #111. What a
                      clean costs depends on the ROOM, so it is not something a list of
                      templates can put a single figure against; the standard time is. */}
                  <th className="p-2 text-start font-medium">{t.clColTime}</th>
                  <th className="p-2 text-start font-medium">{t.clColInUse}</th>
                  <th className="p-2 text-start font-medium">{t.clColState}</th>
                  <th className="p-2 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {board.templates.map((row) => (
                  <tr key={row.templateId} className="border-b border-border last:border-0">
                    <td className="p-2">
                      <Bidi>{row.name}</Bidi>
                      <span className="ms-1.5 font-mono text-[11px] text-muted-foreground">
                        {row.code}
                      </span>
                    </td>
                    <td className="p-2">
                      <Bidi>{(locale === 'ar' ? row.appliesToAr : row.appliesTo) ?? '—'}</Bidi>
                    </td>
                    {/* A VERSION NUMBER IS AN IDENTIFIER, not a quantity: v7 is a name.
                        Western digits in both languages, like a room number. */}
                    <td className="p-2 font-mono">
                      {row.liveVersion === null ? '—' : `v${row.liveVersion}`}
                      {row.draftVersion !== null && (
                        <span className="ms-1.5 rounded-full bg-[#fef3c7] px-1.5 py-0.5 text-[10px]
                                         text-[#92400e] dark:bg-[#2a1f07] dark:text-[#fcd34d]">
                          v{row.draftVersion} {t.clStateDraft.toLowerCase()}
                        </span>
                      )}
                    </td>
                    {/* Quantities, so they localise. */}
                    <td className="p-2 tabular-nums">{formatCount(row.liveItems, locale)}</td>
                    <td className="p-2 tabular-nums">
                      {t.clMinutes.replace('{count}', formatCount(row.standardMinutes, locale))}
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {row.inUseSinceUtc === null ? '—' : localDate(row.inUseSinceUtc, locale)}
                    </td>
                    <td className="p-2">
                      {row.liveVersion === null
                        ? <Chip tone="warn">{t.clStateNever}</Chip>
                        : <Chip tone="live">{t.clStateLive}</Chip>}
                    </td>
                    <td className="p-2 text-end whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        className="inline-flex items-center gap-1 rounded-hk border border-border
                                   px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                      >
                        <Pencil className="size-3" />
                        {/* THE BUTTON SAYS WHICH OF THE TWO THINGS IT DOES. Starting a new
                            draft and returning to one somebody left open on Tuesday are
                            different acts, and a single word for both is how the second one
                            gets pressed by accident. */}
                        {row.draftVersion === null ? t.clEdit : t.clEditDraft}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>
    </div>
  )
}

/* ── a new template ───────────────────────────────────────────────────────── */

function NewTemplate({
  propertyId, locale, t, onCancel, onCreated,
}: {
  propertyId: string
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onCreated: (row: ChecklistTemplateRow) => void
}) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      onCreated(await createChecklistTemplate(propertyId, code))
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader title={t.clNewTemplate} icon={<Plus className="size-4" />} />
      <form
        onSubmit={(e) => { e.preventDefault(); if (code.trim() !== '' && !busy) void submit() }}
        className="flex flex-col gap-3 p-3.5"
      >
        <label className="flex flex-col gap-1 text-[12px]">
          <span className="font-medium">{t.clTaskType}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            aria-label={t.clTaskType}
            placeholder="DEPARTURE"
            className="h-9 w-56 rounded-hk border border-input bg-background px-2.5 font-mono text-[13px]"
          />
        </label>

        {error !== null && (
          <div role="alert" className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2
                                       text-[12.5px] text-[#991b1b] dark:border-[#7f1d1d]
                                       dark:bg-[#2a0f0f] dark:text-[#fca5a5]">
            <Bidi>{translateError(error, locale).message}</Bidi>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={code.trim() === '' || busy}
            className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium text-white
                       disabled:bg-muted disabled:text-muted-foreground cursor-pointer"
          >
            {t.clCreate}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-hk border border-border px-3.5 py-2 text-xs font-medium
                       hover:bg-muted cursor-pointer"
          >
            {t.editCancel}
          </button>
        </div>
      </form>
    </Card>
  )
}

function Figure({
  label, value, tone = 'plain',
}: { label: string; value: string; tone?: 'plain' | 'draft' }) {
  return (
    <div
      className={`rounded-hk border px-3 py-1.5
                  ${tone === 'draft'
                    ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#92400e] dark:bg-[#2a1f07]'
                    : 'border-border'}`}
    >
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="ms-2 text-[15px] font-semibold tabular-nums">{value}</span>
    </div>
  )
}

function Chip({ tone, children }: { tone: 'live' | 'warn'; children: React.ReactNode }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px]
                  ${tone === 'live'
                    ? 'bg-[#dcfce7] text-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]'
                    : 'bg-[#fef3c7] text-[#92400e] dark:bg-[#2a1f07] dark:text-[#fcd34d]'}`}
    >
      {children}
    </span>
  )
}

function localDate(iso: string, locale: 'en' | 'ar'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(iso))
}
